import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { act, localId, read } from "./api";
import SegmentPicker from "./SegmentPicker";

export default function SaveEnrichmentSegmentDialog({ batch, api, onClose }) {
  const [mode, setMode] = useState("new");
  const [name, setName] = useState(
    `Enrichment ${new Date(batch.requestedAt).toLocaleString()} — ${batch.subjects.map((s) => s.name).join(", ")}`.slice(
      0,
      255,
    ),
  );
  const [description, setDescription] = useState("");
  const [destination, setDestination] = useState([]);
  const [count, setCount] = useState(null);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  useEffect(() => {
    let active = true;
    setCount(null);
    setError("");
    read(api, "enrichmentSegmentPreview", { id: localId(batch.id) })
      .then((result) => {
        if (active) setCount(result.selected);
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [api, batch.id, refresh]);
  const save = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    try {
      const result = await act(api, "saveEnrichmentSegment", {
        batchId: localId(batch.id),
        ...(mode === "new"
          ? { name: name.trim(), description }
          : { segmentId: destination[0].id }),
      });
      enqueueSnackbar(
        `Added ${result.added}; already present ${result.alreadyPresent}.`,
        {
          variant: "success",
          action: (
            <Button
              color="inherit"
              component={RouterLink}
              to={`/superuser/marketing/segments/${result.segment.id}`}
            >
              Open segment
            </Button>
          ),
        },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(
        `${err.message}${mode === "new" && !err.graphQLErrors?.length ? " Check Segments before trying again: the save may have completed." : ""}`,
        { variant: "error" },
      );
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Save enrichment to segment</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography>
            Batch: {new Date(batch.requestedAt).toLocaleString()} ·{" "}
            {batch.subjects.map((s) => s.name).join(", ")}
          </Typography>
          <Typography>
            Only publicly confirmed or provider-verified contacts are included,
            across all result pages.
          </Typography>
          {error ? (
            <Alert
              severity="error"
              action={
                <Button onClick={() => setRefresh((n) => n + 1)}>Retry</Button>
              }
            >
              {error}
            </Alert>
          ) : (
            <Typography role="status">
              {count === null
                ? "Checking verified contacts…"
                : `${count} verified contacts available`}
            </Typography>
          )}
          <Typography variant="body2">
            This saves the contacts available now. Later results or verification
            changes are not added automatically; save to the same segment again
            to add them without duplicates. Counts are checked again when
            saving.
          </Typography>
          <TextField
            select
            label="Destination"
            value={mode}
            disabled={busy}
            onChange={(e) => setMode(e.target.value)}
          >
            <MenuItem value="new">Create a new segment</MenuItem>
            <MenuItem value="existing">Add to an existing segment</MenuItem>
          </TextField>
          {mode === "new" ? (
            <>
              <TextField
                label="Segment name"
                value={name}
                disabled={busy}
                required
                onChange={(e) => setName(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 255 } }}
              />
              <TextField
                label="Description"
                value={description}
                disabled={busy}
                multiline
                onChange={(e) => setDescription(e.target.value)}
              />
            </>
          ) : (
            <>
              <SegmentPicker
                api={api}
                entity="enrichmentSegments"
                kind="contact"
                label="Existing segment"
                multiple={false}
                value={destination}
                onChange={setDestination}
                disabled={busy}
              />
              <Typography variant="body2">
                If this segment excludes contacts from a workflow, adding them
                will stop their matching enrollments.
              </Typography>
            </>
          )}
          <Typography variant="body2">
            Saving does not enroll contacts or send emails. Normal eligibility
            and unsubscribe checks still apply.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={
            busy ||
            !count ||
            Boolean(error) ||
            (mode === "new" ? !name.trim() : !destination.length)
          }
        >
          {busy ? "Saving…" : "Save to segment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
